import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbortToeAmendment, Amendment } from 'src/app/core/toe/_models/toe-amendment.model';
import { AbortToeAmendmentService } from 'src/app/core/toe/_services/abort-toe-amendment.service';
import { AmendmentComponent } from '../amendment-dialog.types';
import { PaymentSummaryData, PaymentTermData } from '../payments-info/payments-info.component';
import { WarningModalComponent } from '../warning-modal/warning-modal.component';

@Component({
  selector: 'kt-abort-toe-amendment',
  templateUrl: './abort-toe-amendment.component.html',
  styleUrls: ['./abort-toe-amendment.component.scss']
})
export class AbortToeAmendmentComponent implements OnInit, OnDestroy, AmendmentComponent {
  @Input() inputAmendment: Amendment;
  @Output() onAmendmentRemove: EventEmitter<Amendment> = new EventEmitter();

  public amendment: AbortToeAmendment;
  public form: UntypedFormGroup;

  public currency: string = 'MNT';
  public selectedTermIDs: number[] = [];
  private _totalInclTax: number = 0;
  public balance: number = 0;

  private _paymentSummaryDataSubject: BehaviorSubject<PaymentSummaryData[]> = new BehaviorSubject([]);
  public paymentSummaryData$ = this._paymentSummaryDataSubject.asObservable();
  
  private _terms: any[] = [];
  private _paymentTermDataSubject: BehaviorSubject<PaymentTermData[]> = new BehaviorSubject([]);
  public paymentTermData$ = this._paymentTermDataSubject.asObservable();

  public parties: Array<{type: number, name: string, isDisabled: boolean}> = [];

  private destroy$: Subject<void> = new Subject();
  constructor(
    private dialog: MatDialog,
    private service: AbortToeAmendmentService,
    private formBuilder: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.amendment = (this.inputAmendment as AbortToeAmendment);
    this.selectedTermIDs = this.amendment.selected_term_ids;
    this.service.getToeRelatedInfo(this.amendment.toe_id).pipe(
      takeUntil(this.destroy$)
    ).subscribe(toeRelatedData => {
      this.currency = toeRelatedData.currency;

      const paymentSummaryData: Array<PaymentSummaryData> = [];
      paymentSummaryData.push({designation: 'Valuation Fees', amount: toeRelatedData.valuationFees, isBold: false});
      if (toeRelatedData.additionalCosts > 0) {
        paymentSummaryData.push({designation: 'Additional Costs', amount: toeRelatedData.additionalCosts, isBold: false});
      }
      if (toeRelatedData.discountPercent > 0) {
        paymentSummaryData.push({designation: `Discount (${toeRelatedData.discountPercent}%)`, amount: toeRelatedData.discountPrice, isBold: false});
      }
      paymentSummaryData.push({designation: 'Total (excluding taxes)', amount: toeRelatedData.totalExclTax, isBold: true});
      if (toeRelatedData.vatPercent > 0) {
        paymentSummaryData.push({designation: `VAT (${toeRelatedData.vatPercent}%)`, amount: toeRelatedData.vat, isBold: false});
      }
      if (toeRelatedData.otherPercent > 0) {
        paymentSummaryData.push({designation: `Other taxes (${toeRelatedData.otherPercent}%)`, amount: toeRelatedData.vatOther, isBold: false});
      }
      paymentSummaryData.push({designation: `Total (including taxes)`, amount: toeRelatedData.totalInclTax, isBold: true});
      this._paymentSummaryDataSubject.next(paymentSummaryData);

      this._terms = toeRelatedData.paymentTerms;
      this._paymentTermDataSubject.next(toeRelatedData.paymentTerms.map(p => ({...p, initiallyChecked: false})));

      this._totalInclTax = toeRelatedData.totalInclTax;
      this._updateBalance();

      this.parties = toeRelatedData.parties.map(p => ({...p, isDisabled: false}));
    })
    this.form = this.formBuilder.group({
      reason: [this.amendment.reason, Validators.required],
      payment_arbitration: [this.amendment.payment_arbitration],
      paying_party: [this.amendment.paying_party],
      payed_party: [this.amendment.payed_party],
      paying_amount: [this.amendment.paying_amount],
      comments: [this.amendment.comments],
      disabled_input: [{value: '', disabled: true}],
    });
    const controls = this.form.controls;
    controls.payment_arbitration.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(
      val => {
        if (val == 1 || val == 2) {
          controls.paying_party.setValidators([Validators.required]);
          controls.payed_party.setValidators([Validators.required]);
          controls.paying_amount.setValidators([Validators.required]);
        } else {
          controls.paying_party.clearValidators();
          controls.payed_party.clearValidators();
          controls.paying_amount.clearValidators();
        }
        controls.paying_party.updateValueAndValidity();
        controls.payed_party.updateValueAndValidity();
        controls.paying_amount.updateValueAndValidity();
      }
    )
    controls.paying_party.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      const notSelectedParty = this.parties.find(p => p.type != val);
      controls.payed_party.setValue(notSelectedParty.type, {emitEvent: false});
    });
    controls.payed_party.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      const notSelectedParty = this.parties.find(p => p.type != val);
      controls.paying_party.setValue(notSelectedParty.type, {emitEvent: false});
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeAmendment() {
    const dialogRef = this.dialog.open(WarningModalComponent, {
      width: '400px'
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (!res) {
        return;
      }
      const amendment = this.fetchData();
      this.onAmendmentRemove.emit(amendment);
      this.service.amendmentRemoved();
    })
  }

  fetchData(): Amendment {
    const amendment = new AbortToeAmendment(this.amendment.type, this.amendment.amendment_id, this.amendment.order, this.amendment.toe_id);
    amendment.id = this.amendment.id;
    const controls = this.form.controls;
    amendment.reason = controls.reason.value;
    amendment.payment_arbitration = controls.payment_arbitration.value;
    amendment.paying_party = controls.paying_party.value;
    amendment.payed_party = controls.payed_party.value;
    amendment.paying_amount = controls.paying_amount.value;
    amendment.comments = controls.comments.value;
    amendment.selected_term_ids = this.selectedTermIDs;
    return amendment;
  }
  
  checkValid(): boolean {
    let hasErrors = false;
    const controls = this.form.controls;
    if (this.form.invalid) {
      Object.keys(controls).forEach(controlName => {
        if (!controls[controlName].valid) {
          hasErrors = true;
        }
        controls[controlName].markAsTouched();
      })
    }

    return !hasErrors;
  }

  public selectionChange({change, id}:{change: boolean, id: number}) {
    if (change) {
      this.selectedTermIDs.push(id);
    } else {
      const indx = this.selectedTermIDs.findIndex(tid => tid == id);
      if (indx > -1) {
        this.selectedTermIDs.splice(indx, 1);
      }
    }
    this._updateBalance();
  }

  private _updateBalance() {
    const selectedTerms = this._terms.filter(t => this.selectedTermIDs.includes(t.id))
    const selectedTermSum = selectedTerms.reduce((acc, t) => acc + t.amount, 0);
    this.balance = this._totalInclTax - selectedTermSum;
  }
}
